import { supabaseClient } from './supabaseClient';

// Client orchestration for group deletion (Stage 39). The admin_delete_group RPC does
// the DB work (archive / restore / ordered hard-delete + trace + snapshot) and returns
// what the client must finish: the storage blobs to purge and the attested members to
// warn about. The DB and storage are separate systems — an FK cascade / SQL delete can
// remove payment_proofs ROWS but never the Storage OBJECTS, so the client purges them.

const PROOF_BUCKET = 'payment-proofs';

// mode: 'delete' (empty->hard-delete, history->archive) | 'restore' | 'hard_delete'.
// Returns the RPC result: { action, storage_prefix, proof_paths, attested_members, ... }.
export async function runGroupDeletion(groupId, mode, confirmToken = null) {
  const { data, error } = await supabaseClient.rpc('admin_delete_group', {
    p_group_id: groupId,
    p_mode: mode,
    p_confirm_token: confirmToken,
  });
  if (error) throw error;
  return data;
}

// Purge the proof blobs a hard-delete left behind. Uses the EXACT paths the RPC
// enumerated (payment_proofs.path + payments.proof_path) — precise: only known proof
// objects are removed, nothing else under the prefix. BEST-EFFORT: the DB rows are
// already gone, so a storage failure is a harmless orphaned-blob leftover, not a
// data-integrity problem — we surface it but never throw (the deletion still succeeded).
export async function purgeGroupProofs(rpcResult) {
  const paths = Array.isArray(rpcResult?.proof_paths) ? rpcResult.proof_paths.filter(Boolean) : [];
  if (!paths.length) return { attempted: 0, removed: 0, error: null };
  try {
    // Supabase Storage remove() caps at ~1000 keys/call; chunk defensively.
    let removed = 0, firstError = null;
    const failedPaths = [];
    for (let i = 0; i < paths.length; i += 900) {
      const chunk = paths.slice(i, i + 900);
      const { error } = await supabaseClient.storage.from(PROOF_BUCKET).remove(chunk);
      if (error) { firstError = firstError || error.message; failedPaths.push(...chunk); }
      else removed += chunk.length;
    }
    if (failedPaths.length) {
      // Log the exact leftover objects so they can be cleaned up later (harmless orphans).
      // eslint-disable-next-line no-console
      console.warn('[groupDeletion] proof blobs not removed from storage:', { bucket: PROOF_BUCKET, error: firstError, paths: failedPaths });
    }
    return { attempted: paths.length, removed, error: firstError, failedPaths };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[groupDeletion] storage purge threw:', e.message, { paths });
    return { attempted: paths.length, removed: 0, error: e.message || 'storage purge failed', failedPaths: paths };
  }
}

// Names of members with a certified attestation in this group — for the export-first
// prompt before a permanent delete. Reads attestations + members directly (the billing
// admin can read both), resolved BEFORE the delete while members still exist.
export async function fetchAttestedMemberNames(groupId) {
  const { data: atts } = await supabaseClient
    .from('attestations').select('member_db_id').eq('group_id', groupId).not('member_db_id', 'is', null);
  const ids = [...new Set((atts || []).map(a => a.member_db_id))];
  if (!ids.length) return [];
  const { data: mem } = await supabaseClient.from('members').select('id, name').in('id', ids);
  return (mem || []).map(m => m.name).filter(Boolean);
}
